// @flow

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import zxcvbn from 'zxcvbn';

import styles from './styles.module.scss';

type Props = {
  password: string,
  onStrengthChange?: (strength: number, isPwned: boolean) => void,
  userInputs?: Array<string>,
  isInvalid?: boolean,
};

const PasswordStrengthMeter = ({ password, onStrengthChange, userInputs = [], isInvalid = false }: Props) => {
  const [strength, setStrength] = useState(0);
  const [isPwned, setIsPwned] = useState(false);
  const [checkingPwned, setCheckingPwned] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Store the latest callback in a ref to avoid recreating effects
  const callbackRef = useRef(onStrengthChange);
  useEffect(() => {
    callbackRef.current = onStrengthChange;
  }, [onStrengthChange]);

  // Memoize user inputs array to prevent unnecessary effect triggers
  const userInputsKey = useMemo(() => JSON.stringify(userInputs), [userInputs]);

  // Separate effect for password strength calculation (synchronous)
  useEffect(() => {
    if (!password || password.length < 4) {
      setStrength(0);
      setFeedback(null);
      return;
    }

    // If form validation fails, force weak strength
    if (isInvalid) {
      setStrength(0);
      setFeedback(null);
      return;
    }

    const result = zxcvbn(password, userInputs);
    setStrength(result.score);
    setFeedback(result.feedback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, userInputsKey, isInvalid]);

  // Separate effect for breach checking (asynchronous, debounced)
  useEffect(() => {
    if (!password || password.length < 4) {
      setIsPwned(false);
      setCheckingPwned(false);
      if (callbackRef.current) {
        callbackRef.current(0, false);
      }
      return;
    }

    // Skip API call if form validation fails
    if (isInvalid) {
      setIsPwned(false);
      setCheckingPwned(false);
      return;
    }

    let cancelled = false;
    const abortController = new AbortController();

    const checkPwned = async () => {
      if (cancelled) {
        return;
      }

      setCheckingPwned(true);

      try {
        // Hash the password with SHA-1
        const encoder = new TextEncoder();
        const data = encoder.encode(password);
        const hashBuffer = await window.crypto.subtle.digest('SHA-1', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('')
          .toUpperCase();

        // Use k-anonymity: only send first 5 chars of hash
        const prefix = hashHex.substring(0, 5);
        const suffix = hashHex.substring(5);

        // Query HIBP API with abort signal
        const response = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
          signal: abortController.signal,
        });

        if (cancelled) {
          return;
        }

        const text = await response.text();

        // Check if our hash suffix appears in the results
        const breached = text.split('\n').some((line) => {
          const [hashSuffix] = line.split(':');
          return hashSuffix === suffix;
        });

        if (!cancelled) {
          setIsPwned(breached);
          setCheckingPwned(false);
        }
      } catch (error) {
        if (error.name === 'AbortError' || cancelled) {
          return;
        }
        if (!cancelled) {
          setIsPwned(false);
          setCheckingPwned(false);
        }
      }
    };

    // Debounce the pwned check
    const timer = setTimeout(checkPwned, 500);

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
      clearTimeout(timer);
      abortController.abort();
    };
  }, [password, isInvalid]);

  // Separate effect to notify parent of changes
  useEffect(() => {
    if (callbackRef.current && password && password.length >= 4) {
      callbackRef.current(strength, isPwned);
    }
  }, [password, strength, isPwned]);

  if (!password || password.length < 4) {
    return null;
  }

  const getStrengthLabel = () => {
    switch (strength) {
      case 0:
        return 'app.password.strength.veryWeak';
      case 1:
        return 'app.password.strength.weak';
      case 2:
        return 'app.password.strength.fair';
      case 3:
        return 'app.password.strength.good';
      case 4:
        return 'app.password.strength.strong';
      default:
        return '';
    }
  };

  const getStrengthClass = () => {
    if (isPwned) return styles.pwned;
    switch (strength) {
      case 0:
      case 1:
        return styles.weak;
      case 2:
        return styles.fair;
      case 3:
        return styles.good;
      case 4:
        return styles.strong;
      default:
        return '';
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.meterWrapper}>
        <div className={`${styles.meter} ${getStrengthClass()}`}>
          <div className={styles.meterFill} style={{ width: `${(strength + 1) * 20}%` }} />
        </div>
        <span className={`${styles.label} ${getStrengthClass()}`}>
          <FormattedMessage id={getStrengthLabel()} />
        </span>
      </div>

      {isPwned && (
        <div className={styles.warning}>
          <FormattedMessage id="app.password.pwned.warning" />
        </div>
      )}

      {checkingPwned && (
        <div className={styles.checking}>
          <FormattedMessage id="app.password.checking.breach" />
        </div>
      )}

      {feedback && feedback?.warning && <div className={styles.suggestion}>{feedback.warning}</div>}
    </div>
  );
};

export default PasswordStrengthMeter;
